import React, { useState, useMemo } from "react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";
import "./../../../assets/css/purchase/datatable.css";

export default function CallTable({ calls = [], onEdit, onDelete, onAdd }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const formatThaiDateTime = (utcString) => {
    if (!utcString) return "-";
    try {
      const yyyy = parseInt(utcString.substring(0, 4)) + 543;
      const MM = utcString.substring(5, 7);
      const dd = utcString.substring(8, 10);
      const hh = utcString.substring(11, 13);
      const mm = utcString.substring(14, 16);
      return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
    } catch {
      return "-";
    }
  };

  const formatThaiDateOnly = (utcString) => {
    if (!utcString) return "-";
    try {
      const s = utcString.toString().split("T")[0];
      const [y, m, d] = s.split("-");
      return `${d}/${m}/${parseInt(y) + 543}`;
    } catch {
      return "-";
    }
  };

  const formatTime = (val) => {
    if (!val) return "";
    try {
      if (val.includes("T")) {
        const m = val.match(/T(\d{2}:\d{2})/);
        return m ? m[1] : "";
      }
      const [h, mi] = val.split(":");
      return `${h.padStart(2, "0")}:${mi.padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  const callsWithOrder = useMemo(() => {
    const sortedAsc = [...calls].sort((a, b) => a.Call_Id - b.Call_Id);
    return sortedAsc.map((c, index) => ({
      ...c,
      orderIndex: index + 1
    }));
  }, [calls]);

  const callsWithSortDate = useMemo(() => {
    return callsWithOrder.map((c) => {
      let dt = 0;

      // Use Call_Date_Raw for sorting (ISO string) if available, fallback to Call_Date
      const dateForSorting = c.Call_Date_Raw || c.Call_Date;

      if (c.Call_Type === 1) {
        if (dateForSorting) {
          // Check if it's already formatted (contains "/") or ISO string
          if (typeof dateForSorting === 'string' && dateForSorting.includes("/")) {
            // It's formatted, try to parse or use Appoint_Date + Time_Start
            if (c.Appoint_Date) {
              const base = new Date(c.Appoint_Date);
              let hh = 0, mm = 0;
              if (c.Time_Start) {
                let t = c.Time_Start;
                if (t.includes("T")) t = t.split("T")[1].substring(0, 5);
                const [h, m] = t.split(":");
                hh = Number(h);
                mm = Number(m);
              }
              base.setHours(hh, mm, 0, 0);
              dt = base.getTime();
            }
          } else {
            // It's ISO string, use directly
            dt = new Date(dateForSorting).getTime();
          }
        }
      } else if (c.Call_Type === 2) {
        if (c.Call_Result && c.Call_Result.trim() && dateForSorting) {
          // Has result: use Call_Date_Raw or Call_Date
          if (typeof dateForSorting === 'string' && dateForSorting.includes("/")) {
            // Formatted, use Appoint_Date + Time_Start
            if (c.Appoint_Date) {
              const base = new Date(c.Appoint_Date);
              let hh = 0, mm = 0;
              if (c.Time_Start) {
                let t = c.Time_Start;
                if (t.includes("T")) t = t.split("T")[1].substring(0, 5);
                const [h, m] = t.split(":");
                hh = Number(h);
                mm = Number(m);
              }
              base.setHours(hh, mm, 0, 0);
              dt = base.getTime();
            }
          } else {
            dt = new Date(dateForSorting).getTime();
          }
        } else if (c.Appoint_Date) {
          // No result: use Appoint_Date + Time_Start
          const base = new Date(c.Appoint_Date);
          let hh = 0, mm = 0;

          if (c.Time_Start) {
            let t = c.Time_Start;
            if (t.includes("T")) t = t.split("T")[1].substring(0, 5);
            const [h, m] = t.split(":");
            hh = Number(h);
            mm = Number(m);
          }

          base.setHours(hh, mm, 0, 0);
          dt = base.getTime();
        }
      }

      return { ...c, sortDate: dt };
    });
  }, [callsWithOrder]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let data = [...callsWithSortDate];

    if (!sortConfig.key) {
      return data.sort((a, b) => b.Call_Id - a.Call_Id);
    }

    return data.sort((a, b) => {
      let x = a[sortConfig.key];
      let y = b[sortConfig.key];

      if (sortConfig.key === "sortDate") {
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      if (typeof x === "number" && typeof y === "number")
        return sortConfig.direction === "asc" ? x - y : y - x;

      x = x ? x.toString() : "";
      y = y ? y.toString() : "";

      return sortConfig.direction === "asc"
        ? x.localeCompare(y, "th")
        : y.localeCompare(x, "th");
    });
  }, [callsWithSortDate, sortConfig]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column)
      return <span style={{ marginLeft: 4, color: "#fff" }}>▲▼</span>;
    return sortConfig.direction === "asc" ? (
      <span style={{ marginLeft: 4, color: "#fff" }}>▲</span>
    ) : (
      <span style={{ marginLeft: 4, color: "#fff" }}>▼</span>
    );
  };

  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pageData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(91vh - 320px)",
        overflow: "hidden",
        padding: "0 0 0 10px",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }
        th { cursor: pointer; user-select: none; }
        table.table { 
          border-collapse: separate !important;
          border-spacing: 0 !important;
          width: 100%; 
          min-width: 1100px; 
        }
        tbody tr:hover { background: #f5f7ff !important; }
        th, td { white-space: nowrap; }
        th span { font-size: 11px !important; }
        thead th {
          position: sticky;
          top: 0;
          z-index: 5;
          background: #598AE4;
          color: white;
          white-space: nowrap !important;
        }
        .sticky-action {
          position: sticky;
          right: 0;
          background: white !important;
          z-index: 20;
          isolation: isolate;
          box-shadow: -3px 0 5px rgba(0,0,0,0.12);
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }

        .call-result-col, .call-remark-col {
          min-width: 200px;
          max-width: 300px;
          white-space: normal !important;
          word-break: break-word;
        }
        .call-result-col.has-data, .call-remark-col.has-data {
          text-align: left !important;
        }
        .call-result-col.no-data, .call-remark-col.no-data {
          text-align: center !important;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
        <h5 className="m-0 fw-bold">ประวัติการโทรหาลูกค้า</h5>
        <button
          onClick={onAdd}
          className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5"
        >
          + บันทึกผลการโทร
        </button>
      </div>

      <div
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1, padding: "0 0 0 5px" }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th onClick={() => handleSort("Call_Id")}>
                ครั้งที่ <SortIcon column="Call_Id" />
              </th>
              <th onClick={() => handleSort("Call_Result")}>
                ผลการโทร <SortIcon column="Call_Result" />
              </th>
              <th onClick={() => handleSort("Call_Remark")}>
                หมายเหตุเพิ่มเติม <SortIcon column="Call_Remark" />
              </th>
              <th onClick={() => handleSort("sortDate")}>
                วันที่โทรหาลูกค้า <SortIcon column="sortDate" />
              </th>
              <th onClick={() => handleSort("Sale_Emp_Name")}>
                พนักงานขาย <SortIcon column="Sale_Emp_Name" />
              </th>
              <th className="sticky-action">การจัดการ</th>
            </tr>
          </thead>

          <tbody>
            {pageData.length > 0 ? (
              pageData.map((c, i) => {
                const callResult = c.Call_Result?.trim();
                // Check if Call_Date is already formatted (contains "/") or ISO string
                const callDate = c.Call_Date && typeof c.Call_Date === 'string' && c.Call_Date.includes("/")
                  ? c.Call_Date  // Already formatted
                  : formatThaiDateTime(c.Call_Date_Raw || c.Call_Date);  // Format from raw or fallback
                const appointDate = formatThaiDateOnly(c.Appoint_Date);
                const t1 = formatTime(c.Time_Start);
                const t2 = formatTime(c.Time_End);

                const appointDisplay = (() => {
                  const hasResult = c.Call_Result && c.Call_Result.trim();

                  if (c.Call_Type === 1) return callDate;

                  if (c.Call_Type === 2) {
                    if (hasResult) return callDate;
                    // If no result, try to use Appoint_Date + Time_Start - Time_End
                    // But if Appoint_Date is invalid/null, fallback to Call_Date
                    if (appointDate && appointDate !== "-" && !appointDate.includes("undefined")) {
                      if (t1 || t2) return `${appointDate} ${t1}${t2 ? ` - ${t2}` : ""}`;
                      return appointDate;
                    }
                    // Appoint_Date is invalid/null, use Call_Date instead
                    return callDate || "-";
                  }

                  return "-";
                })();

                return (
                  <tr key={c.Call_Id || i}>
                    <td>{c.orderIndex}</td>

                    <td className={`call-result-col ${callResult ? "has-data" : "no-data"}`}>
                      {callResult ? (
                        callResult
                      ) : (
                        <span className="bg-red-500 text-white px-2 py-1 rounded-full">
                          ยังไม่มีผลการโทร
                        </span>
                      )}
                    </td>

                    <td className={`call-remark-col ${c.Call_Remark?.trim() ? "has-data" : "no-data"}`} title={c.Call_Remark || ""}>
                      {c.Call_Remark || "-"}
                    </td>

                    <td>{appointDisplay}</td>
                    <td>{c.Sale_Emp_Name || "-"}</td>

                    <td className="sticky-action">
                      <div className="d-flex justify-content-center gap-2 flex-nowrap">
                        {!c.Call_Result?.trim() && (
                          <button
                            onClick={() => onEdit?.(c)}
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#598AE4" }}
                          >
                            บันทึกผล
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-4 text-muted">
                  ไม่มีข้อมูลการโทร
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ position: "relative", zIndex: 20, background: "white" }}>
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
}
